import React from 'react'
import { Card, Typography } from '@surecompanies/core_components'
import {
  ClockCircleOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { formatDuration, rateColor } from '../../shared/utils'
import { BotType } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import type { DashboardSummary } from '../../shared/types'
import { BotTypeTag } from './BotTypeTag'
import styles from './PaymentBotSummary.module.scss'

const { Text } = Typography

interface Props {
  summary: DashboardSummary
  overallFirstSucceeded: number
  overallRescued: number
}

// Fleet-level KPI strip for the summary view: bot counts, total benefits,
// process status, success rate (first attempt + rescued), and avg time.
export function PaymentBotKpiStrip({
  summary,
  overallFirstSucceeded,
  overallRescued,
}: Props) {
  const successPct = summary.overallEventualRate
  const firstAttemptPct = summary.overallFirstAttemptRate
  const secondAttemptPct = Math.max(
    0,
    Math.round((successPct - firstAttemptPct) * 10) / 10
  )
  const failedPct =
    summary.succeededBenefits + summary.failedBenefits === 0
      ? 0
      : Math.max(0, Math.round((100 - successPct) * 10) / 10)

  return (
    <div className={styles.kpiGrid}>
      <Card bodyStyle={{ padding: 20 }} data-testid="kpi-total-bots">
        <div className={styles.kpiCardInner}>
          <div>
            <div className={styles.kpiLabel}>Total Bots</div>
            <div className={styles.kpiValue}>{summary.totalBots}</div>
            <div className={styles.kpiSubFlex}>
              <BotTypeTag
                botType={BotType.WEB_AGENT}
                count={summary.webAgentBots}
              />
              <BotTypeTag botType={BotType.IVR} count={summary.ivrBots} />
            </div>
          </div>
          <ThunderboltOutlined className={styles.kpiIconBlue} />
        </div>
      </Card>

      <Card bodyStyle={{ padding: 20 }} data-testid="kpi-total-benefits">
        <div className={styles.kpiCardInner}>
          <div>
            <div className={styles.kpiLabel}>Total Benefits</div>
            <div className={styles.kpiValue}>
              {summary.totalBenefits.toLocaleString()}
            </div>
            <div className={styles.kpiSub}>Unique benefits this period</div>
          </div>
          <TeamOutlined className={styles.kpiIconCyan} />
        </div>
      </Card>

      <Card bodyStyle={{ padding: 20 }} data-testid="kpi-process-status">
        <div className={styles.kpiLabel}>Bot Process Status</div>
        <div className={styles.botStatusRow}>
          <Text>Success</Text>
          <Text strong style={{ color: rateColor(successPct) }}>
            {summary.succeededBenefits.toLocaleString()} ({successPct}%)
          </Text>
        </div>
        <div className={styles.botStatusSubRow}>
          <Text type="secondary">First Attempt</Text>
          <Text type="secondary">
            {overallFirstSucceeded.toLocaleString()} ({firstAttemptPct}%)
          </Text>
        </div>
        <div className={styles.botStatusSubRow}>
          <Text type="secondary">2nd+ Attempts</Text>
          <Text type="secondary">
            {overallRescued.toLocaleString()} ({secondAttemptPct}%)
          </Text>
        </div>
        <div className={styles.botStatusFailedRow}>
          <Text>Failed</Text>
          <Text strong className={styles.textDanger}>
            {summary.failedBenefits.toLocaleString()} ({failedPct}%)
          </Text>
        </div>
      </Card>

      <Card bodyStyle={{ padding: 20 }} data-testid="kpi-avg-time">
        <div className={styles.kpiCardInner}>
          <div>
            <div className={styles.kpiLabel}>Avg Processing Time</div>
            <div className={styles.kpiValue}>
              {formatDuration(summary.medianProcessingTimeSec)}
            </div>
            <div className={styles.kpiSub}>Across all bots</div>
          </div>
          <ClockCircleOutlined className={styles.kpiIconOrange} />
        </div>
      </Card>
    </div>
  )
}
